import { useEffect } from "react";
import useStoreType from "./useStoreType";
import { getCustomTheme } from "../../api";
import { StoreBrandingType } from "../../types/misc";
import { ThemeOptions } from "@mui/material/styles";
import {
  buildThemeFromServerTheme,
  ServerThemeType,
  useUpdateCustomTheme,
} from "@brandclub/common-ui";
import { AppConfigTheme } from "@/redux/types";
import { useAppSelector } from "@/redux/hooks";
import { ThemeDraft } from "../ThemeDraft";
import { produce } from "immer";

const isValidTheme = (
  theme: ServerThemeType | undefined
): theme is ServerThemeType => {
  if (!theme) {
    return false;
  }
  return theme.primary && theme.secondary ? true : false;
};

const buildDtcTheme = (serverTheme: ServerThemeType): ThemeOptions => {
  const parsedTheme = buildThemeFromServerTheme(serverTheme, true);
  if (!parsedTheme) {
    throw new Error("Failed to build theme from server theme");
  }
  const theme = produce(parsedTheme, (draft) => {
    if (typeof draft.typography === "object") {
      // we never want to override the htmlFontSize (this is only for the DTC sites)
      delete draft.typography.htmlFontSize;
    }
    if (draft.palette?.background) {
      // we don't want to override the background color of the webapp.
      delete draft.palette.background;
    }
  });
  return theme;
};

const buildCustomStoreTheme = (theme: AppConfigTheme): ThemeOptions => ({
  palette: {
    primary: {
      main: theme.primaryColor,
    },
    secondary: {
      main: theme.secondaryColor,
    },
    text: {
      primary: theme.primaryColor,
    },
  },
  typography: {
    fontSize: theme.baseFontSize,
  },
});

export const useCustomTheme = () => {
  const [, setTheme] = useUpdateCustomTheme();
  const storeType = useStoreType();
  const serverBrandAppTheme = useAppSelector(
    ({ appConfig }) => appConfig?.brandAppThemeConfig?.theme
  );

  useEffect(() => {
    const fetchDtcTheme = async () => {
      const appId = window.location.hostname;
      const theme =
        (await ThemeDraft.getTheme(appId)) ?? (await getCustomTheme());
      if (!isValidTheme(theme)) {
        console.warn("Invalid DTC theme", theme);
        return;
      }
      setTheme(buildDtcTheme(theme));
      if (
        theme?.typography?.fontFamily &&
        theme?.settings?.fonts?.fontFamilyLink
      ) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = theme.settings.fonts.fontFamilyLink;
        document.head.appendChild(link);
      }
      if (theme?.customCss) {
        const style = document.createElement("style");
        style.textContent = theme.customCss;
        document.head.appendChild(style);
      }
    };
    if (storeType === StoreBrandingType.CustomDTCStore) {
      fetchDtcTheme();
      return;
    }
    if (storeType === StoreBrandingType.CustomStore) {
      /**
       * theme for custom stores (NOT DTC)
       * ex: roccoandroxie.brandclub.com
       */
      serverBrandAppTheme &&
        setTheme(buildCustomStoreTheme(serverBrandAppTheme));
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeType, serverBrandAppTheme]);
};
